import { useMutation, useQuery } from "@apollo/client";
import { ConfigurationKey, ConfigurationValue } from "@salon/types";
import { useCallback, useMemo } from "react";
import { toast } from "sonner";
import { GET_SALON_CONFIGURATIONS, UPDATE_SALON_CONFIGURATION } from "@/graphql/configuration";
import { useSalonStore } from "@/lib/store/salon";
import { useConfigurationSubscription } from "./use-configuration-subscription";

interface UseConfigResult<K extends ConfigurationKey> {
  value: ConfigurationValue<K> | undefined;
  isLoading: boolean;
  error: string | null;
  update: (newValue: ConfigurationValue<K>) => Promise<void>;
  reload: () => Promise<void>;
}

/**
 * Hook for accessing and updating a specific configuration key with real-time updates
 *
 * Simplified version that relies entirely on Apollo cache as the single source of truth.
 *
 * Features:
 * - Type-safe configuration access
 * - Real-time updates via GraphQL subscriptions
 * - Optimistic updates for better UX
 * - Error handling and recovery
 * - Automatic loading of configuration data
 *
 * @example
 * ```typescript
 * const { value, isLoading, update } = useConfig('online_booking.enabled');
 *
 * // Access the value (type-safe)
 * const isOnlineBookingEnabled = value; // boolean | undefined
 *
 * // Update the value
 * await update(true);
 * ```
 */
export function useConfig<K extends ConfigurationKey>(
  key: K,
  options?: {
    /** Enable real-time subscription (default: true) */
    subscription?: boolean;
    /** Skip loading configuration data (default: false) */
    skip?: boolean;
  }
): UseConfigResult<K> {
  const { subscription = true, skip = false } = options || {};

  const { currentSalon } = useSalonStore();
  const salonId = currentSalon?.id;

  // Parse category from key for efficient querying
  const category = useMemo(() => {
    return key.split(".")[0];
  }, [key]);

  // GraphQL query for configuration data - Apollo cache is the source of truth
  const {
    data,
    loading: queryLoading,
    error: queryError,
    refetch,
  } = useQuery(GET_SALON_CONFIGURATIONS, {
    variables: {
      salonId: salonId ?? "",
      category,
    },
    skip: skip || !salonId,
  });

  // Parse the value from GraphQL response
  const value = useMemo(() => {
    if (!data?.getSalonConfigurations?.configurations) {
      return undefined;
    }

    const config = data.getSalonConfigurations.configurations.find((c) => c.key === key);
    if (!config) {
      return undefined;
    }

    // Parse value based on data type
    let parsedValue: any = config.value;

    switch (config.dataType) {
      case "boolean":
        parsedValue = config.value.toLowerCase() === "true";
        break;
      case "int":
        parsedValue = Number.parseInt(config.value, 10);
        break;
      case "string[]":
        parsedValue = config.value
          .split(",")
          .map((v) => v.trim())
          .filter((v) => v.length > 0);
        break;
      default:
        parsedValue = config.value;
        break;
    }

    return parsedValue as ConfigurationValue<K>;
  }, [data, key]);

  // GraphQL mutation for updates
  const [updateConfigMutation, { loading: mutationLoading }] = useMutation(
    UPDATE_SALON_CONFIGURATION,
    {
      optimisticResponse: (vars) => ({
        updateSalonConfiguration: {
          __typename: "SalonConfiguration" as const,
          key: vars.input.key,
          value: vars.input.value,
          dataType: vars.input.dataType,
          category,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
      }),
      update: (cache, { data: mutationData }) => {
        if (mutationData?.updateSalonConfiguration && salonId) {
          // Update the cache with the new configuration
          cache.updateQuery(
            {
              query: GET_SALON_CONFIGURATIONS,
              variables: { salonId, category },
            },
            (existingData) => {
              if (!existingData?.getSalonConfigurations) {
                return existingData;
              }

              const updatedConfig = mutationData.updateSalonConfiguration;
              const configurations = existingData.getSalonConfigurations.configurations || [];

              // Find and update the configuration
              const configIndex = configurations.findIndex((c) => c.key === updatedConfig.key);

              if (configIndex >= 0) {
                // Update existing configuration - preserve createdAt from existing
                const existingConfig = configurations[configIndex];
                const newConfigurations = [...configurations];
                newConfigurations[configIndex] = {
                  ...updatedConfig,
                  createdAt: existingConfig.createdAt,
                };

                return {
                  getSalonConfigurations: {
                    ...existingData.getSalonConfigurations,
                    configurations: newConfigurations,
                  },
                };
              } else {
                // Add new configuration - use current timestamp for createdAt
                return {
                  getSalonConfigurations: {
                    ...existingData.getSalonConfigurations,
                    configurations: [
                      ...configurations,
                      {
                        ...updatedConfig,
                        createdAt: updatedConfig.updatedAt,
                      },
                    ],
                  },
                };
              }
            }
          );
        }
      },
      onError: (error) => {
        console.error("Failed to update configuration:", error);
        toast.error(`Failed to update configuration: ${error.message}`);
      },
    }
  );

  // Real-time subscription
  const { loading: subscriptionLoading } = useConfigurationSubscription({
    salonId: salonId ?? "",
    keys: [key],
    enabled: subscription && !!salonId && !skip,
  });

  // Update function
  const update = useCallback(
    async (newValue: ConfigurationValue<K>) => {
      if (!salonId) {
        throw new Error("No salon selected");
      }

      // Determine data type for the backend
      const getDataType = (value: any): string => {
        if (typeof value === "boolean") return "boolean";
        if (typeof value === "number") return "int";
        if (Array.isArray(value)) return "string[]";

        // Check for enum types based on key patterns
        if (key.includes(".mode")) {
          if (key.includes("online_booking")) return "online_booking_mode";
          if (key.includes("booking_behavior")) return "booking_behavior_mode";
          if (key.includes("outcome_tracking")) return "outcome_tracking_mode";
        }

        if (key.includes(".cutoff") || key.includes("_time")) return "time";

        return "string";
      };

      // Serialize value for the backend
      const serializeValue = (value: any): string => {
        if (typeof value === "boolean") return value ? "true" : "false";
        if (typeof value === "number") return value.toString();
        if (Array.isArray(value)) return value.join(", ");
        return String(value);
      };
      await updateConfigMutation({
        variables: {
          salonId,
          input: {
            key,
            value: serializeValue(newValue),
            dataType: getDataType(newValue),
          },
        },
      });

      toast.success("Configuration updated successfully");
    },
    [salonId, key, updateConfigMutation]
  );

  // Reload function
  const reload = useCallback(async () => {
    if (refetch) {
      await refetch();
    }
  }, [refetch]);

  // Combine loading states
  const isLoading = queryLoading || mutationLoading || subscriptionLoading;

  // Get error message
  const error = queryError?.message || null;

  return {
    value,
    isLoading,
    error,
    update,
    reload,
  };
}
